import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, SlidersHorizontal } from 'lucide-react';
import { movimientosApi } from '@/services/movimientosApi';
import { almacenesApi } from '@/services/almacenesApi';
import { InventoryItemPicker } from './InventoryItemPicker';
import type { InventoryItemRefDto, MovimientoCreateRequest, TipoMovimiento } from '@/types/inventario';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Tipo inicial sugerido. Si null/undefined arranca en ENTRADA. */
  defaultTipo?: TipoMovimiento | null;
  /** Ítem prefijado (p.e. al disparar desde Kardex). */
  defaultItem?: InventoryItemRefDto | null;
}

const TIPOS: { id: TipoMovimiento; label: string; icon: typeof ArrowDownToLine; activeClass: string }[] = [
  { id: 'ENTRADA', label: 'Entrada', icon: ArrowDownToLine, activeClass: 'bg-emerald-50 border-emerald-300 text-emerald-700' },
  { id: 'SALIDA', label: 'Salida', icon: ArrowUpFromLine, activeClass: 'bg-red-50 border-red-300 text-red-700' },
  { id: 'TRASLADO', label: 'Traslado', icon: ArrowLeftRight, activeClass: 'bg-blue-50 border-blue-300 text-blue-700' },
  { id: 'AJUSTE', label: 'Ajuste', icon: SlidersHorizontal, activeClass: 'bg-amber-50 border-amber-300 text-amber-700' },
];

export function MovimientoForm({ open, onClose, defaultTipo, defaultItem }: Props) {
  const queryClient = useQueryClient();
  const [tipo, setTipo] = useState<TipoMovimiento>(defaultTipo ?? 'ENTRADA');
  const [item, setItem] = useState<InventoryItemRefDto | null>(defaultItem ?? null);
  const [origenId, setOrigenId] = useState<number | ''>('');
  const [destinoId, setDestinoId] = useState<number | ''>('');
  const [cantidad, setCantidad] = useState<number | ''>('');
  const [nota, setNota] = useState('');
  const [referencia, setReferencia] = useState('');
  /** Para AJUSTE: 'destino' suma, 'origen' resta. */
  const [ajusteSigno, setAjusteSigno] = useState<'destino' | 'origen'>('destino');

  useEffect(() => {
    if (open) {
      setTipo(defaultTipo ?? 'ENTRADA');
      setItem(defaultItem ?? null);
      setOrigenId('');
      setDestinoId('');
      setCantidad('');
      setNota('');
      setReferencia('');
      setAjusteSigno('destino');
    }
  }, [open, defaultTipo, defaultItem]);

  const { data: almacenes = [] } = useQuery({
    queryKey: ['almacenes'],
    queryFn: () => almacenesApi.findAll(true),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: (req: MovimientoCreateRequest) => movimientosApi.registrar(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventario-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['inventario-stock'] });
      queryClient.invalidateQueries({ queryKey: ['movimientos'] });
      queryClient.invalidateQueries({ queryKey: ['kardex'] });
      queryClient.invalidateQueries({ queryKey: ['productos'] });
      queryClient.invalidateQueries({ queryKey: ['controladores'] });
      queryClient.invalidateQueries({ queryKey: ['pantallas-modulares'] });
      queryClient.invalidateQueries({ queryKey: ['fuentes'] });
      queryClient.invalidateQueries({ queryKey: ['tarjetas-receptoras'] });
      queryClient.invalidateQueries({ queryKey: ['otros-items'] });
      queryClient.invalidateQueries({ queryKey: ['inventario-disponibilidad'] });
      toast.success('Movimiento registrado');
      onClose();
    },
    onError: (err: unknown) => {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)?.error ?? 'Error al registrar movimiento'
        : 'Error al registrar movimiento';
      toast.error(msg);
    },
  });

  const showOrigen = tipo === 'SALIDA' || tipo === 'TRASLADO' || (tipo === 'AJUSTE' && ajusteSigno === 'origen');
  const showDestino = tipo === 'ENTRADA' || tipo === 'TRASLADO' || (tipo === 'AJUSTE' && ajusteSigno === 'destino');

  const helperText = useMemo(() => {
    switch (tipo) {
      case 'ENTRADA': return 'Suma stock al almacén destino. Ej.: compra, devolución de cliente.';
      case 'SALIDA': return 'Resta stock del almacén origen. Ej.: venta, baja, consumo.';
      case 'TRASLADO': return 'Mueve stock de un almacén a otro sin alterar el total.';
      case 'AJUSTE': return ajusteSigno === 'destino'
        ? 'Suma stock por inventario físico (ej. unidades encontradas).'
        : 'Resta stock por inventario físico (ej. unidades faltantes).';
    }
  }, [tipo, ajusteSigno]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!item) { toast.error('Selecciona un ítem'); return; }
    if (cantidad === '' || cantidad <= 0) { toast.error('Cantidad debe ser mayor a 0'); return; }
    if (showOrigen && !origenId) { toast.error('Selecciona almacén origen'); return; }
    if (showDestino && !destinoId) { toast.error('Selecciona almacén destino'); return; }
    if (tipo === 'TRASLADO' && origenId === destinoId) { toast.error('Origen y destino deben ser distintos'); return; }

    mutation.mutate({
      tipo,
      inventoryItemId: item.id,
      almacenOrigenId: showOrigen ? Number(origenId) : null,
      almacenDestinoId: showDestino ? Number(destinoId) : null,
      cantidad: Number(cantidad),
      nota: nota.trim() || null,
      referencia: referencia.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4">Nuevo Movimiento</h3>

        <div className="grid grid-cols-4 gap-2 mb-4">
          {TIPOS.map((t) => {
            const Icon = t.icon;
            const active = tipo === t.id;
            const palette = active
              ? t.activeClass
              : 'border-gray-200 text-gray-500 hover:bg-gray-50';
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTipo(t.id)}
                className={`flex flex-col items-center gap-1 p-2 border rounded-lg text-xs font-medium transition-colors ${palette}`}
              >
                <Icon size={18} />
                {t.label}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-gray-500 mb-4">{helperText}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ítem</label>
            <InventoryItemPicker value={item} onChange={setItem} />
          </div>

          {tipo === 'AJUSTE' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de ajuste</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAjusteSigno('destino')}
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm ${ajusteSigno === 'destino' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'border-gray-300 text-gray-600'}`}
                >
                  + Sumar
                </button>
                <button
                  type="button"
                  onClick={() => setAjusteSigno('origen')}
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm ${ajusteSigno === 'origen' ? 'bg-red-50 border-red-300 text-red-700' : 'border-gray-300 text-gray-600'}`}
                >
                  − Restar
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {showOrigen && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Almacén origen</label>
                <select
                  value={origenId}
                  onChange={(e) => setOrigenId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                >
                  <option value="">Seleccionar…</option>
                  {almacenes.map((a) => (
                    <option key={a.id} value={a.id}>{a.nombre}</option>
                  ))}
                </select>
              </div>
            )}
            {showDestino && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Almacén destino</label>
                <select
                  value={destinoId}
                  onChange={(e) => setDestinoId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                >
                  <option value="">Seleccionar…</option>
                  {almacenes.map((a) => (
                    <option key={a.id} value={a.id}>{a.nombre}</option>
                  ))}
                </select>
              </div>
            )}
            <div className={tipo === 'TRASLADO' ? 'sm:col-span-2' : ''}>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad</label>
              <input
                type="number"
                min={1}
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Referencia <span className="text-gray-400 font-normal">(opcional)</span></label>
            <input
              type="text"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="Nº factura, OC, ticket…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nota <span className="text-gray-400 font-normal">(opcional)</span></label>
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Registrando…' : 'Registrar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
